import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import * as QRCode from 'qrcode';
import {
  ArrowRight,
  AlertTriangle,
  BadgeCheck,
  BarChart3,
  CalendarDays,
  CalendarPlus,
  Check,
  Clipboard,
  DoorOpen,
  ExternalLink,
  Loader2,
  LogIn,
  Mail,
  MapPin,
  Megaphone,
  Minus,
  MousePointerClick,
  Phone,
  Plus,
  QrCode,
  Search,
  ScanLine,
  Share2,
  ShieldCheck,
  Sparkles,
  Ticket,
  UserPlus,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { getRequiredAccessToken, nextEventsApi } from './api';
import './NextEvents.css';

const NEXT_LOGO = 'https://nexteventslasvegas.com/__l5e/assets-v1/5254011e-1363-40d1-98ad-8f512cc54317/next-events-logo-v3.png';
const HERO_IMAGE = 'https://nexteventslasvegas.com/assets/hero-lasvegas-CVUZGrgr.jpg';
const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim();
const GOOGLE_SCRIPT_ID = 'next-google-identity';

type GoogleCredentialResponse = {
  credential?: string;
  select_by?: string;
};

type GoogleProfile = {
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(input: { client_id: string; callback: (response: GoogleCredentialResponse) => void; auto_select?: boolean; cancel_on_tap_outside?: boolean }): void;
          renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
          cancel(): void;
        };
      };
    };
  }
}

function currentHost() {
  return typeof window === 'undefined' ? 'next.event.tools' : window.location.host;
}

function loadGoogleIdentity() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Google RSVP is browser-only'));
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Google RSVP failed to load')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google RSVP failed to load'));
    document.head.appendChild(script);
  });
}

function decodeGoogleProfile(credential?: string): GoogleProfile {
  if (!credential) return {};
  try {
    const payload = credential.split('.')[1];
    if (!payload) return {};
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const json = decodeURIComponent(Array.from(atob(normalized), (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''));
    return JSON.parse(json) as GoogleProfile;
  } catch {
    return {};
  }
}

function splitGoogleName(profile: GoogleProfile) {
  const parts = (profile.name ?? '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: profile.given_name ?? parts[0] ?? profile.email?.split('@')[0] ?? 'Guest',
    lastName: profile.family_name ?? (parts.length > 1 ? parts.slice(1).join(' ') : ''),
  };
}

function workosUserProfile(user: unknown) {
  const value = (user && typeof user === 'object' ? user : {}) as Record<string, unknown>;
  const email = typeof value.email === 'string' ? value.email : undefined;
  const full = typeof value.name === 'string' ? value.name.trim().split(/\s+/).filter(Boolean) : [];
  const firstName =
    (typeof value.firstName === 'string' && value.firstName)
    || (typeof value.first_name === 'string' && value.first_name)
    || full[0]
    || email?.split('@')[0]
    || 'Guest';
  const lastName =
    (typeof value.lastName === 'string' && value.lastName)
    || (typeof value.last_name === 'string' && value.last_name)
    || (full.length > 1 ? full.slice(1).join(' ') : '');
  return { firstName, lastName, email };
}

function passUrl(pass: any) {
  const origin = typeof window === 'undefined' ? 'https://next.event.tools' : window.location.origin;
  const base = typeof window !== 'undefined' && window.location.pathname.startsWith('/next') ? '/next' : '';
  return `${origin}${base}/door/${pass?.eventSlug ?? ''}?pass=${encodeURIComponent(pass?.code ?? '')}`;
}

function calendarHref(pass: any) {
  const start = pass?.startsAt ? new Date(pass.startsAt) : null;
  const end = start ? new Date(start.getTime() + 4 * 60 * 60 * 1000) : null;
  const fmt = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: pass?.eventTitle ?? 'Next Events',
    details: `Guest list pass ${pass?.code ?? ''}`,
    location: pass?.venueName ?? 'Las Vegas, NV',
  });
  if (start && end) params.set('dates', `${fmt(start)}/${fmt(end)}`);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function useAsyncQuery<T>(load: () => Promise<T>, deps: React.DependencyList) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;
    setData(undefined);
    setError(null);
    load()
      .then((value) => {
        if (active) setData(value);
      })
      .catch((queryError) => {
        if (active) setError(queryError);
      });
    return () => {
      active = false;
    };
  }, [...deps, version]);

  if (error) throw error;
  return {
    data,
    reload: () => setVersion((current) => current + 1),
  };
}

function useBasePath() {
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
  return pathname === '/next' || pathname.startsWith('/next/') ? '/next' : '';
}

function useNextPath() {
  const base = useBasePath();
  return (path: string) => `${base}${path === '/' ? '' : path}` || '/';
}

function formatDate(startsAt?: number) {
  if (!startsAt) return 'Date announced soon';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(startsAt));
}

function fullName(firstName: string, lastName?: string) {
  return [firstName, lastName].filter(Boolean).join(' ');
}

function statusTone(status: string) {
  if (status === 'approved' || status === 'checked_in' || status === 'public') return 'text-[#00e0ea] border-[#00e0ea66]';
  if (status === 'pending' || status === 'waitlisted') return 'text-[#ff3df8] border-[#ff3df866]';
  if (status === 'denied' || status === 'closed') return 'text-red-300 border-red-400/40';
  return 'text-[#a99ec0] border-white/10';
}

function normalizeImportRows(text: string) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [namePart = '', phoneOrEmail = '', status = 'approved'] = line.split(',').map((part) => part.trim());
      const [firstName = 'Guest', ...lastParts] = namePart.split(/\s+/);
      const isEmail = phoneOrEmail.includes('@');
      return {
        firstName,
        lastName: lastParts.join(' ') || undefined,
        phone: isEmail ? undefined : phoneOrEmail || undefined,
        email: isEmail ? phoneOrEmail : undefined,
        status: ['pending', 'approved', 'waitlisted', 'denied'].includes(status) ? status as any : 'approved',
      };
    });
}

function GoogleRsvpButton({ onCredential, disabled }: { onCredential: (credential: string) => void; disabled?: boolean }) {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const credentialRef = useRef(onCredential);
  const [ready, setReady] = useState(typeof window !== 'undefined' && Boolean(window.google?.accounts?.id));
  const [error, setError] = useState('');

  useEffect(() => {
    credentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google RSVP is not configured yet.');
      return;
    }
    let cancelled = false;
    loadGoogleIdentity()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !buttonRef.current) return;
        setReady(true);
        buttonRef.current.innerHTML = '';
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => {
            if (response.credential) credentialRef.current(response.credential);
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'filled_black',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          width: Math.min(360, buttonRef.current.clientWidth || 360),
          logo_alignment: 'left',
        });
      })
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : 'Google RSVP failed to load'));
    return () => {
      cancelled = true;
      window.google?.accounts?.id?.cancel();
    };
  }, []);

  return (
    <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
      <div className="next-google-button" ref={buttonRef} />
      {!ready && !error && <span className="mt-2 inline-flex items-center gap-2 text-sm text-[#a99ec0]"><Loader2 className="h-4 w-4 animate-spin" /> Loading Google RSVP</span>}
      {error && <p className="mt-2 text-xs text-red-200">{error}</p>}
    </div>
  );
}

function PartyStepper({ value, max, onChange }: { value: number; max: number; onChange: (value: number) => void }) {
  const update = (next: number) => onChange(Math.max(1, Math.min(max, next)));
  return (
    <div className="next-party-stepper">
      <button type="button" aria-label="Decrease party size" onClick={() => update(value - 1)}><Minus className="h-4 w-4" /></button>
      <span>{value}</span>
      <button type="button" aria-label="Increase party size" onClick={() => update(value + 1)}><Plus className="h-4 w-4" /></button>
    </div>
  );
}

function VirtualPass({ event, result }: { event: any; result: any }) {
  const pass = result.pass ?? {
    code: result.rsvpId,
    holderName: 'Guest',
    eventTitle: event.title,
    eventSlug: event.slug,
    startsAt: event.startsAt,
    venueName: event.venueName,
    partySize: 1,
    externalTicketUrl: event.externalTicketUrl,
  };
  const [qr, setQr] = useState('');
  const shareText = `${pass.eventTitle} guest-list pass ${pass.code}`;

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(passUrl(pass), {
      margin: 1,
      width: 220,
      color: { dark: '#020105', light: '#f6f4fb' },
    }).then((value) => {
      if (active) setQr(value);
    });
    return () => {
      active = false;
    };
  }, [pass.code]);

  const sharePass = async () => {
    const url = passUrl(pass);
    if (navigator.share) {
      await navigator.share({ title: pass.eventTitle, text: shareText, url });
      return;
    }
    await navigator.clipboard.writeText(url);
  };

  return (
    <div className="next-pass-wrap">
      <div className="next-virtual-pass">
        <div className="next-pass-scanline" />
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="next-kicker mb-3">Guest pass</p>
            <h2 className="next-display text-3xl font-black">{pass.eventTitle}</h2>
            <p className="mt-3 text-sm text-[#a99ec0]">{formatDate(pass.startsAt)} {pass.venueName ? `// ${pass.venueName}` : ''}</p>
          </div>
          <div className={`next-pass-status ${statusTone(result.status)}`}>{result.status}</div>
        </div>

        <div className="mt-7 grid gap-5 md:grid-cols-[1fr_160px]">
          <div className="space-y-4">
            <div>
              <span className="next-pass-label">Holder</span>
              <strong>{pass.holderName}</strong>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="next-pass-label">Party</span>
                <strong>{pass.partySize}</strong>
              </div>
              <div>
                <span className="next-pass-label">Code</span>
                <strong>{pass.code}</strong>
              </div>
            </div>
          </div>
          <div className="next-pass-qr">
            {qr ? <img src={qr} alt="Guest pass QR code" /> : <Loader2 className="h-8 w-8 animate-spin text-[#ff3df8]" />}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <a href={calendarHref(pass)} target="_blank" rel="noreferrer" className="next-outline-button inline-flex items-center justify-center gap-2 px-4 py-3 text-xs">
          <CalendarPlus className="h-4 w-4" /> Calendar
        </a>
        <button type="button" onClick={sharePass} className="next-outline-button inline-flex items-center justify-center gap-2 px-4 py-3 text-xs">
          <Share2 className="h-4 w-4" /> Share
        </button>
        {pass.externalTicketUrl ? (
          <a href={pass.externalTicketUrl} target="_blank" rel="noreferrer" className="next-gradient-button inline-flex items-center justify-center gap-2 px-4 py-3 text-xs">
            <Wallet className="h-4 w-4" /> Ticket Link
          </a>
        ) : (
          <button type="button" className="next-gradient-button inline-flex items-center justify-center gap-2 px-4 py-3 text-xs">
            <ScanLine className="h-4 w-4" /> Door Pass
          </button>
        )}
      </div>
    </div>
  );
}

function Shell({ children, compact = false }: { children: React.ReactNode; compact?: boolean }) {
  const to = useNextPath();
  return (
    <div className="next-engine">
      <header className="next-header">
        <div className="next-shell flex items-center justify-between py-3">
          <Link to={to('/')} className="flex items-center gap-3">
            <img src={NEXT_LOGO} alt="Next Events" className="h-14 w-auto object-contain md:h-16" />
          </Link>
          <nav className="hidden items-center gap-5 md:flex">
            <Link className="next-nav-link" to={to('/')}>Engine</Link>
            <a className="next-nav-link" href={`${to('/')}#events`}>Events</a>
            <Link className="next-nav-link" to={to('/join')}>Join List</Link>
            <Link className="next-nav-link" to={to('/promoter')}>Promoters</Link>
            <Link className="next-nav-link" to={to('/admin')}>Admin</Link>
          </nav>
        </div>
      </header>
      <main className={compact ? 'next-shell py-8' : ''}>{children}</main>
    </div>
  );
}

function LoadingState() {
  return (
    <Shell compact>
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ff3df8]" />
      </div>
    </Shell>
  );
}

function NextProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, user } = useAuth();
  const location = useLocation();
  const to = useNextPath();

  if (isLoading) return <LoadingState />;
  if (!user) {
    return <Navigate to={to('/signin')} state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function NextEventsUnavailable() {
  const to = useNextPath();
  return (
    <Shell compact>
      <div className="mx-auto flex min-h-[68vh] max-w-3xl items-center">
        <div className="next-card next-glow w-full p-6 md:p-8">
          <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#ff3df866] bg-[#ff3df812] text-[#ff3df8]">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <p className="next-kicker mb-3">Engine status</p>
          <h1 className="next-display text-3xl font-black uppercase leading-tight md:text-5xl">
            Guest list engine is temporarily offline.
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-[#cfc7df] md:text-base">
            The Next Events interface is live, but the data service is not accepting requests right now.
            RSVP, promoter, and door tools will come back online as soon as the backend is restored.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button type="button" onClick={() => window.location.reload()} className="next-gradient-button px-5 py-3 text-xs">
              Retry Connection
            </button>
            <Link to={to('/join')} className="next-outline-button inline-flex items-center gap-2 px-5 py-3 text-xs">
              Join List <UserPlus className="h-4 w-4" />
            </Link>
            <a href="mailto:info@nexteventslasvegas.com" className="next-nav-link inline-flex items-center px-2 py-3">
              Contact Next Events
            </a>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function LandingPage() {
  const to = useNextPath();
  const { data: landing } = useAsyncQuery(() => nextEventsApi.publicLanding(currentHost()), []);
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  const [activeModule, setActiveModule] = useState('rsvp');

  if (landing === undefined) return <LoadingState />;
  const events = (landing as any).events ?? [];
  const featuredEvent = events[0] ?? {
    title: 'Next Events guest list',
    slug: '',
    description: 'Promoter-ready RSVP, attribution, approvals, and door flow for Las Vegas nightlife.',
    startsAt: undefined,
    venueName: 'Las Vegas, NV',
    tags: ['Next Events'],
    flyerImageUrl: HERO_IMAGE,
  };
  const eventPath = featuredEvent.slug ? to(`/e/${featuredEvent.slug}`) : to('/join');
  const moduleTabs = [
    {
      id: 'rsvp',
      label: 'RSVP',
      icon: MousePointerClick,
      eyebrow: 'Guest capture',
      title: 'One-tap RSVP that still captures clean contact data.',
      copy: 'Guests can start with Google, fall back to phone or email, and leave with a QR pass instead of another form.',
      actionLabel: 'Open guest flow',
      actionPath: eventPath,
      points: ['Google prefill', 'Phone fallback', 'Consent source', 'Pass issued'],
    },
    {
      id: 'promoter',
      label: 'Promote',
      icon: Megaphone,
      eyebrow: 'Referral links',
      title: 'Give every promoter a cockpit, not a spreadsheet.',
      copy: 'Promoters see share links, QR codes, starts, submissions, approvals, check-ins, and source credit.',
      actionLabel: 'View promoter portal',
      actionPath: to('/promoter'),
      points: ['Unique links', 'QR share cards', 'Approval status', 'Door credit'],
    },
    {
      id: 'door',
      label: 'Door',
      icon: ScanLine,
      eyebrow: 'Check-in mode',
      title: 'Door staff get the fast path: search, scan, and verify.',
      copy: 'The mobile door view keeps approved guests searchable and warns staff before duplicate check-ins happen.',
      actionLabel: 'Open door mode',
      actionPath: featuredEvent.slug ? to(`/door/${featuredEvent.slug}`) : to('/admin'),
      points: ['QR scanning', 'Duplicate warning', 'Status filters', 'Promoter credit'],
    },
    {
      id: 'crm',
      label: 'CRM',
      icon: Users,
      eyebrow: 'Audience memory',
      title: 'Every RSVP becomes reusable nightlife intelligence.',
      copy: 'Contacts dedupe by phone and email, carry consent, tags, latest RSVP context, and source attribution.',
      actionLabel: 'Join list preview',
      actionPath: to('/join'),
      points: ['Dedupe contacts', 'Consent stored', 'Tags and sources', 'Next-event ready'],
    },
  ];
  const activeModuleData = moduleTabs.find((module) => module.id === activeModule) ?? moduleTabs[0];
  const ActiveModuleIcon = activeModuleData.icon;

  const quickJoin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    await nextEventsApi.joinList({
      host: currentHost(),
      firstName: email.split('@')[0] || 'Guest',
      email,
      marketingConsent: true,
    });
    setJoined(true);
    setEmail('');
  };

  return (
    <Shell>
      <section className="next-app-home">
        <img src={HERO_IMAGE} alt="Las Vegas nightlife" className="next-app-backdrop" />
        <div className="next-app-wash" />

        <div className="next-app-frame">
          <aside className="next-app-rail" aria-label="Engine modules">
            <div className="next-rail-mark">
              <img src={NEXT_LOGO} alt="Next Events" />
            </div>
            <div className="next-rail-tabs">
              {moduleTabs.map((module) => {
                const ModuleIcon = module.icon;
                return (
                  <button
                    key={module.id}
                    type="button"
                    aria-pressed={activeModule === module.id}
                    className={`next-rail-tab ${activeModule === module.id ? 'is-active' : ''}`}
                    onClick={() => setActiveModule(module.id)}
                  >
                    <ModuleIcon className="h-5 w-5" />
                    <span>{module.label}</span>
                  </button>
                );
              })}
            </div>
            <Link to={to('/admin')} className="next-rail-plus" aria-label="Open admin">
              <Plus className="h-5 w-5" />
            </Link>
          </aside>

          <div className="next-app-workspace">
            <div className="next-app-topbar">
              <div className="min-w-0">
                <p className="next-kicker mb-2">Next.event.tools</p>
                <h1>Nightlife operating system</h1>
              </div>
              <div className="next-top-actions">
                <Link to={eventPath} className="next-gradient-button inline-flex items-center gap-2 px-4 py-3 text-xs">
                  <Ticket className="h-4 w-4" /> RSVP
                </Link>
                <Link to={to('/promoter')} className="next-outline-button inline-flex items-center gap-2 px-4 py-3 text-xs">
                  <Share2 className="h-4 w-4" /> Share
                </Link>
              </div>
            </div>

            <div className="next-app-grid">
              <section className="next-panel next-live-panel">
                <div className="next-panel-header">
                  <div>
                    <span className="next-app-label">Tonight command</span>
                    <h2>{featuredEvent.title}</h2>
                  </div>
                  <span className="next-live-dot"><span /> Live</span>
                </div>

                <div className="next-event-strip">
                  <div className="next-event-strip-art">
                    <img src={featuredEvent.flyerImageUrl || HERO_IMAGE} alt={featuredEvent.title} />
                  </div>
                  <div className="next-event-strip-copy">
                    <p>{featuredEvent.description}</p>
                    <div className="next-event-strip-meta">
                      <span><CalendarDays className="h-4 w-4" /> {formatDate(featuredEvent.startsAt)}</span>
                      <span><MapPin className="h-4 w-4" /> {featuredEvent.venueName ?? 'Las Vegas, NV'}</span>
                    </div>
                    <div className="next-action-row">
                      <Link to={eventPath} className="next-gradient-button inline-flex items-center gap-2 px-4 py-3 text-xs">
                        Guest flow <ArrowRight className="h-4 w-4" />
                      </Link>
                      <Link to={featuredEvent.slug ? to(`/door/${featuredEvent.slug}`) : to('/admin')} className="next-outline-button inline-flex items-center gap-2 px-4 py-3 text-xs">
                        Door mode <ScanLine className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="next-ops-tiles">
                  <div><span>Views</span><strong>1.9k</strong><em>+22%</em></div>
                  <div><span>RSVPs</span><strong>412</strong><em>301 approved</em></div>
                  <div><span>Promoters</span><strong>18</strong><em>9 active</em></div>
                  <div><span>Door rate</span><strong>86%</strong><em>258 scanned</em></div>
                </div>
              </section>

              <aside className="next-phone-panel">
                <div className="next-phone-shell">
                  <div className="next-phone-camera" />
                  <div className="next-phone-top">
                    <span>Guest pass</span>
                    <BadgeCheck className="h-5 w-5 text-[#00e0ea]" />
                  </div>
                  <div className="next-phone-art">
                    <img src={featuredEvent.flyerImageUrl || HERO_IMAGE} alt={`${featuredEvent.title} pass`} />
                  </div>
                  <div className="next-phone-pass">
                    <p className="next-kicker mb-2">Fast entry</p>
                    <h2>{featuredEvent.title}</h2>
                    <div className="next-phone-code">
                      <div>
                        <span>Referral</span>
                        <strong>NEXT-THRILLER</strong>
                      </div>
                      <div className="next-phone-qr"><QrCode className="h-12 w-12" /></div>
                    </div>
                  </div>
                  <div className="next-phone-actions">
                    <button type="button"><Wallet className="h-4 w-4" /> Wallet</button>
                    <button type="button"><Share2 className="h-4 w-4" /> Share</button>
                  </div>
                </div>
              </aside>
            </div>

            <section className="next-module-shell">
              <div className="next-module-tabs" role="tablist" aria-label="Engine workflow">
                {moduleTabs.map((module) => {
                  const ModuleIcon = module.icon;
                  return (
                    <button
                      key={module.id}
                      type="button"
                      role="tab"
                      aria-selected={activeModule === module.id}
                      className={activeModule === module.id ? 'is-active' : ''}
                      onClick={() => setActiveModule(module.id)}
                    >
                      <ModuleIcon className="h-4 w-4" />
                      <span>{module.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="next-module-body">
                <div className="next-module-copy">
                  <div className="next-module-icon"><ActiveModuleIcon className="h-6 w-6" /></div>
                  <p className="next-app-label">{activeModuleData.eyebrow}</p>
                  <h2>{activeModuleData.title}</h2>
                  <p>{activeModuleData.copy}</p>
                  <Link to={activeModuleData.actionPath} className="next-gradient-button inline-flex items-center gap-2 px-4 py-3 text-xs">
                    {activeModuleData.actionLabel} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="next-module-checklist">
                  {activeModuleData.points.map((point) => (
                    <div key={point}>
                      <Check className="h-4 w-4" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="next-app-lower-grid">
              <div className="next-panel">
                <div className="next-panel-header">
                  <div>
                    <span className="next-app-label">Promoter board</span>
                    <h2>Attribution that feels alive.</h2>
                  </div>
                  <Link to={to('/promoter')} className="next-nav-link">Portal</Link>
                </div>
                <div className="next-promoter-board">
                  {[
                    ['NEXT-THRILLER', '118 approved', '82 checked in'],
                    ['VIP-FRIDAY', '64 approved', '51 checked in'],
                    ['HOUSE-LIST', '119 approved', '94 checked in'],
                  ].map(([code, approved, checked]) => (
                    <div key={code}>
                      <span>{code}</span>
                      <strong>{approved}</strong>
                      <em>{checked}</em>
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={quickJoin} className="next-panel next-capture-panel">
                <span className="next-app-label">CRM capture</span>
                <h2>Build the list before doors open.</h2>
                <p>Capture the guest, credit the promoter, and keep the contact for the next event.</p>
                <label className="next-label mt-5">Mailing list preview</label>
                <div className="next-capture-row">
                  <div className="next-capture-input">
                    <Mail className="h-4 w-4" />
                    <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="promoter@nightlife.com" />
                  </div>
                  <button className="next-gradient-button px-5 py-3 text-xs" type="submit">
                    {joined ? 'Added' : 'Join'}
                  </button>
                </div>
              </form>
            </section>

            <section id="events" className="next-events-dock">
              <div className="next-panel-header">
                <div>
                  <span className="next-app-label">Event apps</span>
                  <h2>Live pages in the engine.</h2>
                </div>
                <Link to={to('/join')} className="next-nav-link">General list</Link>
              </div>

              {events.length === 0 ? (
                <div className="next-empty-state">
                  <h3>Engine ready for seed data.</h3>
                  <p>Sign in to admin and run setup to create the first Next Events tenant and events.</p>
                </div>
              ) : (
                <div className="next-event-app-list">
                  {events.map((event: any) => (
                    <article key={event._id} className="next-event-app-card">
                      <div className="next-event-app-art">
                        <img src={event.flyerImageUrl || HERO_IMAGE} alt={event.title} />
                      </div>
                      <div className="next-event-app-copy">
                        <span>{event.tags?.[0] ?? 'Next Events'}</span>
                        <h3>{event.title}</h3>
                        <p>{formatDate(event.startsAt)}</p>
                      </div>
                      <div className="next-event-app-actions">
                        <Link to={to(`/e/${event.slug}`)} className="next-gradient-button inline-flex items-center gap-2 px-4 py-3 text-xs">
                          RSVP <ArrowRight className="h-4 w-4" />
                        </Link>
                        {event.externalTicketUrl && (
                          <a href={event.externalTicketUrl} target="_blank" rel="noreferrer" className="next-outline-button inline-flex items-center gap-2 px-4 py-3 text-xs">
                            Tickets <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>

        <nav className="next-mobile-dock" aria-label="Mobile engine shortcuts">
          {moduleTabs.slice(0, 4).map((module) => {
            const ModuleIcon = module.icon;
            return (
              <button
                key={module.id}
                type="button"
                aria-pressed={activeModule === module.id}
                className={activeModule === module.id ? 'is-active' : ''}
                onClick={() => setActiveModule(module.id)}
              >
                <ModuleIcon className="h-5 w-5" />
                <span>{module.label}</span>
              </button>
            );
          })}
        </nav>
      </section>
    </Shell>
  );
}

function PublicEventPage() {
  const { eventSlug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { getAccessToken, isLoading: authLoading, signIn, user } = useAuth();
  const referralCode = searchParams.get('ref') ?? (typeof window !== 'undefined' ? localStorage.getItem('nextReferralCode') : '') ?? '';
  const { data: eventData } = useAsyncQuery(() => nextEventsApi.publicEvent(currentHost(), eventSlug, referralCode), [eventSlug, referralCode]);
  const [started, setStarted] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    phone: '',
    email: '',
    partySize: 1,
    marketingConsent: true,
  });

  const markStarted = () => {
    if (started) return;
    setStarted(true);
    void nextEventsApi.trackReferral({ host: currentHost(), code: referralCode, eventSlug, type: 'start', metadata: { page: 'event' } });
  };

  const submitRsvp = async (payload: Record<string, unknown>) => {
    markStarted();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await nextEventsApi.submitRsvp({
        host: currentHost(),
        eventSlug,
        referralCode,
        partySize: Number(form.partySize) || 1,
        marketingConsent: form.marketingConsent,
        smsConsent: Boolean(form.phone),
        emailConsent: true,
        ...payload,
      });
      setResult(response);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'RSVP did not go through. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitGoogleRsvp = async (credential: string) => {
    const profile = decodeGoogleProfile(credential);
    const name = splitGoogleName(profile);
    await submitRsvp({
      googleIdToken: credential,
      firstName: name.firstName,
      lastName: name.lastName || undefined,
      email: profile.email,
      phone: form.phone || undefined,
      answers: { source: 'google_one_tap' },
    });
  };

  const submitWorkosRsvp = async (overrides: Record<string, unknown> = {}) => {
    const profile = workosUserProfile(user);
    const token = await getRequiredAccessToken(getAccessToken);
    await submitRsvp({
      workosAccessToken: token,
      firstName: profile.firstName,
      lastName: profile.lastName || undefined,
      email: profile.email,
      phone: (overrides.phone as string | undefined) || form.phone || undefined,
      partySize: Number(overrides.partySize) || Number(form.partySize) || 1,
      answers: { source: 'workos_google' },
    });
  };

  const beginGoogleRsvp = async () => {
    if (!user) {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          `nextPendingRsvp:${eventSlug}`,
          JSON.stringify({ partySize: form.partySize, phone: form.phone, referralCode }),
        );
      }
      await signIn({
        state: {
          returnTo: typeof window === 'undefined' ? `/e/${eventSlug}` : `${window.location.pathname}${window.location.search}`,
        },
      });
      return;
    }
    await submitWorkosRsvp();
  };

  useEffect(() => {
    if (eventData === undefined || !user || result || isSubmitting || typeof window === 'undefined') return;
    if (!(eventData as any).event) return;
    const key = `nextPendingRsvp:${eventSlug}`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return;
    window.localStorage.removeItem(key);
    let saved: Record<string, unknown> = {};
    try {
      saved = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      saved = {};
    }
    void submitWorkosRsvp(saved);
  }, [eventData, eventSlug, user]);

  const onManualSubmit = async (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();
    await submitRsvp({
      firstName: form.firstName,
      phone: form.phone || undefined,
      email: form.email || undefined,
      answers: { source: 'manual_fast_rsvp' },
    });
  };

  if (eventData === undefined) return <LoadingState />;
  const event = (eventData as any).event;
  if (!event) {
    return (
      <Shell compact>
        <div className="next-card p-8">
          <h1 className="next-display text-3xl font-bold">Event not found.</h1>
          <p className="mt-3 text-[#a99ec0]">This event may be private, closed, or not seeded yet.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell compact>
      <div className="grid gap-7 lg:grid-cols-[0.78fr_1.22fr]">
        <aside className="next-card next-event-art overflow-hidden">
          {event.flyerImageUrl ? (
            <img src={event.flyerImageUrl} alt={event.title} className="w-full bg-black object-contain" />
          ) : (
            <div className="aspect-[4/5] bg-black/40" />
          )}
          <div className="p-5">
            <p className="next-kicker mb-2">{event.status === 'closed' ? 'List Closed' : event.status === 'waitlist' ? 'Waitlist Open' : 'Guest List'}</p>
            <h1 className="next-display text-3xl font-black">{event.title}</h1>
            <div className="mt-4 space-y-2 text-sm text-[#a99ec0]">
              <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#ff3df8]" /> {formatDate(event.startsAt)}</p>
              {event.venueName && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#00e0ea]" /> {event.venueName}</p>}
            </div>
            <p className="mt-4 text-sm leading-6 text-[#d8d0e8]">{event.description}</p>
          </div>
        </aside>

        <section className="next-rsvp-stage">
          {result ? (
            <div className="next-card next-glow p-5 md:p-7">
              <div className="mb-5 flex items-center gap-3 text-[#00e0ea]">
                <BadgeCheck className="h-6 w-6" />
                <span className="next-kicker text-[#00e0ea]">Request received</span>
              </div>
              <VirtualPass event={event} result={result} />
            </div>
          ) : (
            <div className="next-card next-glow p-5 md:p-7">
              <div className="next-fast-pass-preview">
                <div className="next-pass-scanline" />
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="next-kicker mb-3">1 tap RSVP</p>
                    <h2 className="next-display text-3xl font-black md:text-4xl">Claim your guest-list pass.</h2>
                  </div>
                  <Ticket className="h-9 w-9 text-[#00e0ea]" />
                </div>
                <div className="mt-6 grid gap-4 md:grid-cols-[1fr_180px]">
                  <div className="space-y-4">
                    <div className="next-pass-row">
                      <MousePointerClick className="h-5 w-5 text-[#ff3df8]" />
                      <span>Google fills name and verified email.</span>
                    </div>
                    <div className="next-pass-row">
                      <ShieldCheck className="h-5 w-5 text-[#00e0ea]" />
                      <span>Referral and promoter credit stay attached.</span>
                    </div>
                    <div className="next-pass-row">
                      <QrCode className="h-5 w-5 text-[#ff3df8]" />
                      <span>Pass appears immediately after request.</span>
                    </div>
                  </div>
                  <div>
                    <span className="next-pass-label">Party size</span>
                    <PartyStepper value={form.partySize} max={event.maxPartySize ?? 6} onChange={(partySize) => setForm({ ...form, partySize })} />
                  </div>
                </div>
              </div>

              {referralCode && (
                <div className="mt-5 border border-[#00e0ea55] bg-[#00e0ea12] p-3 text-sm text-[#d8fbff]">
                  Referral code active: <span className="font-bold">{referralCode}</span>
                </div>
              )}

              <div className="mt-5">
                <button type="button" onClick={beginGoogleRsvp} disabled={isSubmitting || authLoading} className="next-gradient-button inline-flex w-full items-center justify-center gap-2 px-6 py-4 text-sm disabled:opacity-60">
                  {user ? 'Finish RSVP with Google' : 'Continue with Google'} <ShieldCheck className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                <label className="next-slim-field">
                  <Phone className="h-4 w-4 text-[#00e0ea]" />
                  <input inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Optional SMS number" />
                </label>
                <label className="flex items-center gap-3 border border-[#ff3df826] bg-[#02010599] px-4 py-3 text-xs uppercase tracking-[0.12em] text-[#a99ec0]">
                  <input type="checkbox" checked={form.marketingConsent} onChange={(e) => setForm({ ...form, marketingConsent: e.target.checked })} />
                  Event updates
                </label>
              </div>

              {submitError && <div className="mt-4 border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-100">{submitError}</div>}
              {isSubmitting && <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#00e0ea]"><Loader2 className="h-4 w-4 animate-spin" /> Building your pass...</div>}

              <div className="mt-6 border-t border-[#ff3df826] pt-5">
                <button type="button" onClick={() => setManualOpen((open) => !open)} className="next-nav-link inline-flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Use phone or email instead
                </button>
                {manualOpen && (
                  <form onSubmit={onManualSubmit} onFocus={markStarted} className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                    <input className="next-input" required placeholder="First name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                    <input className="next-input" required inputMode="tel" placeholder="Phone or email" value={form.phone || form.email} onChange={(e) => {
                      const value = e.target.value;
                      setForm({ ...form, phone: value.includes('@') ? '' : value, email: value.includes('@') ? value : '' });
                    }} />
                    <button className="next-gradient-button inline-flex items-center justify-center gap-2 px-5 py-3 text-xs" type="submit" disabled={isSubmitting}>
                      RSVP <ArrowRight className="h-4 w-4" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </Shell>
  );
}

function ReferralResolver() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const to = useNextPath();
  const { data: target } = useAsyncQuery(() => nextEventsApi.referralTarget(currentHost(), code), [code]);

  useEffect(() => {
    if (typeof window !== 'undefined' && code) localStorage.setItem('nextReferralCode', code.toUpperCase());
    void nextEventsApi.trackReferral({ host: currentHost(), code, type: 'view', metadata: { path: window.location.pathname } });
  }, [code]);

  useEffect(() => {
    if (target === undefined) return;
    const destination = target?.eventSlug ? `/e/${target.eventSlug}?ref=${code}` : '/join';
    navigate(to(destination), { replace: true });
  }, [target, code, navigate, to]);

  return <LoadingState />;
}

function JoinPage() {
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '' });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await nextEventsApi.joinList({ host: currentHost(), ...form, marketingConsent: true });
    setDone(true);
  };

  return (
    <Shell compact>
      <div className="mx-auto max-w-2xl">
        <div className="next-card p-6 md:p-9">
          <p className="next-kicker mb-3">Stay connected</p>
          <h1 className="next-display text-4xl font-black">Join the mailing list.</h1>
          <p className="mt-4 text-[#a99ec0]">Special invites, approvals, and event announcements from Next Events.</p>
          {done ? (
            <div className="mt-8 border border-[#00e0ea55] bg-[#00e0ea12] p-5">
              <BadgeCheck className="mb-3 h-8 w-8 text-[#00e0ea]" />
              You are on the list.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 grid gap-4 md:grid-cols-2">
              <div>
                <label className="next-label">First name</label>
                <input className="next-input" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              </div>
              <div>
                <label className="next-label">Last name</label>
                <input className="next-input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
              </div>
              <div>
                <label className="next-label">Phone</label>
                <input className="next-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="next-label">Email</label>
                <input className="next-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <button className="next-gradient-button mt-2 px-6 py-4 text-sm md:col-span-2" type="submit">Join List</button>
            </form>
          )}
        </div>
      </div>
    </Shell>
  );
}

function SignInPage() {
  const { signIn } = useAuth();
  return (
    <Shell compact>
      <div className="mx-auto max-w-md next-card p-7 text-center">
        <LogIn className="mx-auto h-10 w-10 text-[#ff3df8]" />
        <h1 className="next-display mt-5 text-3xl font-black">Staff sign in.</h1>
        <p className="mt-3 text-[#a99ec0]">Use Google through WorkOS to access admin, promoter, and door tools.</p>
        <button onClick={() => void signIn()} className="next-gradient-button mt-6 inline-flex items-center gap-2 px-6 py-4 text-sm">
          Sign in with Google <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </Shell>
  );
}

function AdminPage() {
  const { getAccessToken } = useAuth();
  const overviewQuery = useAsyncQuery(async () => {
    const token = await getRequiredAccessToken(getAccessToken);
    return nextEventsApi.adminOverview(currentHost(), token);
  }, [getAccessToken]);
  const overview = overviewQuery.data;
  const [eventForm, setEventForm] = useState({ title: '', startsAt: '', venueName: '', capacity: 300 });
  const [promoterName, setPromoterName] = useState('');
  const [importText, setImportText] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');

  if (overview === undefined) return <LoadingState />;
  const data = overview as any;
  const events = data.events ?? [];
  const rsvps = data.rsvps ?? [];
  const promoters = data.promoters ?? [];
  const links = data.links ?? [];
  const stats = data.stats ?? {};
  const eventById = new Map<string, any>(events.map((event: any) => [String(event._id), event]));

  const runSeed = async () => {
    const token = await getRequiredAccessToken(getAccessToken);
    await nextEventsApi.seedDefaults(token);
    overviewQuery.reload();
  };

  const submitEvent = async (event: React.FormEvent) => {
    event.preventDefault();
    const token = await getRequiredAccessToken(getAccessToken);
    const id = await nextEventsApi.createOrUpdateEvent(currentHost(), token, {
      title: eventForm.title,
      startsAt: eventForm.startsAt ? new Date(eventForm.startsAt).getTime() : undefined,
      venueName: eventForm.venueName || undefined,
      capacity: Number(eventForm.capacity) || undefined,
      status: 'public',
      requirePhone: false,
      requireEmail: false,
      maxPartySize: 6,
    });
    setSelectedEventId(String(id));
    setEventForm({ title: '', startsAt: '', venueName: '', capacity: 300 });
    overviewQuery.reload();
  };

  const submitPromoter = async (event: React.FormEvent) => {
    event.preventDefault();
    const token = await getRequiredAccessToken(getAccessToken);
    await nextEventsApi.createPromoter(currentHost(), token, { name: promoterName });
    setPromoterName('');
    overviewQuery.reload();
  };

  const submitImport = async () => {
    if (!selectedEventId || !importText.trim()) return;
    const token = await getRequiredAccessToken(getAccessToken);
    await nextEventsApi.importGuestList(currentHost(), token, {
      eventId: selectedEventId,
      fileName: 'pasted-list.csv',
      rows: normalizeImportRows(importText),
    });
    setImportText('');
    overviewQuery.reload();
  };

  return (
    <Shell compact>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="next-kicker mb-2">Command center</p>
          <h1 className="next-display text-4xl font-black">Next Events admin.</h1>
        </div>
        <button onClick={runSeed} className="next-outline-button inline-flex items-center gap-2 px-4 py-3 text-xs">
          <Sparkles className="h-4 w-4" /> Seed defaults
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        <Metric icon={CalendarDays} label="Events" value={stats.events ?? 0} />
        <Metric icon={UserPlus} label="Pending" value={stats.pending ?? 0} />
        <Metric icon={BadgeCheck} label="Approved" value={stats.approved ?? 0} />
        <Metric icon={DoorOpen} label="Checked in" value={stats.checkedIn ?? 0} />
        <Metric icon={Ticket} label="Ticket feed" value={stats.externalTicketEvents ?? 0} />
      </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <section className="next-card p-5">
          <h2 className="next-display text-xl font-bold">Create public event</h2>
          <form onSubmit={submitEvent} className="mt-4 grid gap-3">
            <input className="next-input" required placeholder="Event title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} />
            <input className="next-input" type="datetime-local" value={eventForm.startsAt} onChange={(e) => setEventForm({ ...eventForm, startsAt: e.target.value })} />
            <input className="next-input" placeholder="Venue" value={eventForm.venueName} onChange={(e) => setEventForm({ ...eventForm, venueName: e.target.value })} />
            <input className="next-input" type="number" min={1} placeholder="Capacity" value={eventForm.capacity} onChange={(e) => setEventForm({ ...eventForm, capacity: Number(e.target.value) })} />
            <button className="next-gradient-button px-4 py-3 text-xs" type="submit">Create Event</button>
          </form>
        </section>

        <section className="next-card p-5">
          <h2 className="next-display text-xl font-bold">Guest import</h2>
          <p className="mt-2 text-sm text-[#a99ec0]">Paste one guest per line: name, phone or email, status.</p>
          <select className="next-input mt-4" value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)}>
            <option value="">Select event...</option>
            {events.map((event: any) => <option key={event._id} value={event._id}>{event.title}</option>)}
          </select>
          <textarea className="next-input mt-3 min-h-[132px]" value={importText} onChange={(e) => setImportText(e.target.value)} placeholder="Jane Doe, 7025551212, approved" />
          <button onClick={submitImport} className="next-outline-button mt-3 px-4 py-3 text-xs">Import guest list</button>
        </section>
      </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <section className="next-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-[#ff3df826] p-5">
            <h2 className="next-display text-xl font-bold">RSVP queue</h2>
            <span className="text-xs uppercase tracking-[0.18em] text-[#a99ec0]">{rsvps.length} recent</span>
          </div>
          <div className="overflow-x-auto">
            <table className="next-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Event</th>
                  <th>Status</th>
                  <th>Party</th>
                  <th>Referral</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rsvps.map((rsvp: any) => (
                  <tr key={rsvp._id}>
                    <td>{fullName(rsvp.firstName, rsvp.lastName)}</td>
                    <td>{eventById.get(String(rsvp.eventId))?.title ?? 'Event'}</td>
                    <td><span className={`next-status ${statusTone(rsvp.status)}`}>{rsvp.status}</span></td>
                    <td>{rsvp.partySize}</td>
                    <td>{rsvp.referralCode ?? 'direct'}</td>
                    <td>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={async () => {
                          const token = await getRequiredAccessToken(getAccessToken);
                          await nextEventsApi.updateRsvpStatus(currentHost(), token, { rsvpId: rsvp._id, status: 'approved' });
                          overviewQuery.reload();
                        }} className="text-[#00e0ea]"><Check className="h-4 w-4" /></button>
                        <button onClick={async () => {
                          const token = await getRequiredAccessToken(getAccessToken);
                          await nextEventsApi.updateRsvpStatus(currentHost(), token, { rsvpId: rsvp._id, status: 'waitlisted' });
                          overviewQuery.reload();
                        }} className="text-[#ff3df8]"><Users className="h-4 w-4" /></button>
                        <button onClick={async () => {
                          const token = await getRequiredAccessToken(getAccessToken);
                          await nextEventsApi.updateRsvpStatus(currentHost(), token, { rsvpId: rsvp._id, status: 'denied' });
                          overviewQuery.reload();
                        }} className="text-red-300"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-6">
          <div className="next-card p-5">
            <h2 className="next-display text-xl font-bold">Promoters</h2>
            <form onSubmit={submitPromoter} className="mt-4 flex gap-2">
              <input className="next-input" required placeholder="Promoter name" value={promoterName} onChange={(e) => setPromoterName(e.target.value)} />
              <button className="next-gradient-button px-4 text-xs">Add</button>
            </form>
            <div className="mt-4 space-y-3">
              {promoters.map((promoter: any) => (
                <div key={promoter._id} className="border border-[#ff3df826] p-3">
                  <div className="font-bold">{promoter.name}</div>
                  <div className="text-xs uppercase tracking-[0.12em] text-[#a99ec0]">{promoter.code}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="next-card p-5">
            <h2 className="next-display text-xl font-bold">Referral links</h2>
            <div className="mt-4 space-y-3">
              {links.slice(0, 6).map((link: any) => (
                <div key={link._id} className="border border-[#00e0ea2e] p-3 text-sm">
                  <div className="font-bold">{link.code}</div>
                  <div className="truncate text-[#a99ec0]">/r/{link.code}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="next-card p-5">
            <h2 className="next-display text-xl font-bold">Posh bridge</h2>
            <p className="mt-2 text-sm text-[#a99ec0]">Webhook-ready for Posh ticket/order events once the shared secret is configured.</p>
            <div className="mt-4 border border-[#00e0ea2e] p-3 text-sm">
              <div className="truncate text-[#a99ec0]">{nextEventsApi.webhookUrl('posh', currentHost())}</div>
              <button onClick={() => navigator.clipboard.writeText(nextEventsApi.webhookUrl('posh', currentHost()))} className="next-outline-button mt-3 inline-flex items-center gap-2 px-3 py-2 text-[11px]">
                <Clipboard className="h-3 w-3" /> Copy endpoint
              </button>
            </div>
          </div>
        </section>
      </div>
    </Shell>
  );
}

function Metric({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number }) {
  return (
    <div className="next-card p-4">
      <Icon className="mb-3 h-5 w-5 text-[#ff3df8]" />
      <div className="next-display text-3xl font-black">{value}</div>
      <div className="text-xs uppercase tracking-[0.16em] text-[#a99ec0]">{label}</div>
    </div>
  );
}

function PromoterPage() {
  const { getAccessToken } = useAuth();
  const { data: dashboard } = useAsyncQuery(async () => {
    const token = await getRequiredAccessToken(getAccessToken);
    return nextEventsApi.promoterDashboard(currentHost(), token);
  }, [getAccessToken]);
  const to = useNextPath();
  if (dashboard === undefined) return <LoadingState />;
  const data = dashboard as any;

  const copyLink = async (code: string) => {
    const origin = typeof window === 'undefined' ? 'https://next.event.tools' : window.location.origin;
    await navigator.clipboard.writeText(`${origin}${to(`/r/${code}`)}`);
  };

  return (
    <Shell compact>
      <div className="mb-7">
        <p className="next-kicker mb-2">Promoter cockpit</p>
        <h1 className="next-display text-4xl font-black">Track what you move.</h1>
      </div>
      {data.noProfile ? (
        <div className="next-card p-7">
          <Megaphone className="mb-4 h-9 w-9 text-[#ff3df8]" />
          <h2 className="next-display text-2xl font-bold">No promoter profile yet.</h2>
          <p className="mt-3 text-[#a99ec0]">Ask a Next Events admin to connect your Google account to a promoter profile.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-5">
            <Metric icon={BarChart3} label="Views" value={data.stats.views} />
            <Metric icon={UserPlus} label="Starts" value={data.stats.starts} />
            <Metric icon={Ticket} label="Submits" value={data.stats.submits} />
            <Metric icon={BadgeCheck} label="Approved" value={data.stats.approved} />
            <Metric icon={DoorOpen} label="Checked in" value={data.stats.checkedIn} />
          </div>
          <section className="next-card mt-7 p-5">
            <h2 className="next-display text-xl font-bold">Share links</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {data.links.map((link: any) => (
                <div key={link._id} className="border border-[#ff3df826] p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-bold">{link.label ?? link.code}</span>
                    <QrCode className="h-5 w-5 text-[#00e0ea]" />
                  </div>
                  <div className="truncate text-sm text-[#a99ec0]">/r/{link.code}</div>
                  <button onClick={() => copyLink(link.code)} className="next-outline-button mt-3 inline-flex items-center gap-2 px-3 py-2 text-[11px]">
                    <Clipboard className="h-3 w-3" /> Copy link
                  </button>
                </div>
              ))}
            </div>
          </section>
          <section className="next-card mt-7 overflow-hidden">
            <div className="border-b border-[#ff3df826] p-5">
              <h2 className="next-display text-xl font-bold">Attributed RSVPs</h2>
            </div>
            <table className="next-table">
              <thead><tr><th>Name</th><th>Status</th><th>Party</th><th>Code</th></tr></thead>
              <tbody>
                {data.rsvps.map((rsvp: any) => (
                  <tr key={rsvp._id}>
                    <td>{fullName(rsvp.firstName, rsvp.lastName)}</td>
                    <td><span className={`next-status ${statusTone(rsvp.status)}`}>{rsvp.status}</span></td>
                    <td>{rsvp.partySize}</td>
                    <td>{rsvp.referralCode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </Shell>
  );
}

function DoorPage() {
  const { eventSlug = '' } = useParams();
  const { getAccessToken } = useAuth();
  const doorQuery = useAsyncQuery(async () => {
    const token = await getRequiredAccessToken(getAccessToken);
    return nextEventsApi.doorList(currentHost(), eventSlug, token);
  }, [eventSlug, getAccessToken]);
  const data = doorQuery.data;
  const [search, setSearch] = useState('');
  const [lastResult, setLastResult] = useState('');
  if (data === undefined) return <LoadingState />;
  if (!data) return <Shell compact><div className="next-card p-7">Door list not found.</div></Shell>;

  const rows = ((data as any).rsvps ?? []).filter((rsvp: any) => {
    const haystack = `${rsvp.firstName} ${rsvp.lastName ?? ''} ${rsvp.phone ?? ''} ${rsvp.email ?? ''}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  const runCheckIn = async (rsvpId: string) => {
    const token = await getRequiredAccessToken(getAccessToken);
    const result = await nextEventsApi.checkIn(currentHost(), token, rsvpId);
    setLastResult(result.duplicate ? 'Already checked in.' : 'Checked in.');
    doorQuery.reload();
  };

  return (
    <Shell compact>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="next-kicker mb-2">Door mode</p>
          <h1 className="next-display text-3xl font-black">{(data as any).event.title}</h1>
        </div>
        {lastResult && <div className="border border-[#00e0ea55] bg-[#00e0ea12] px-4 py-2 text-sm">{lastResult}</div>}
      </div>
      <div className="next-card p-4">
        <label className="next-label">Search guest</label>
        <div className="flex items-center gap-2">
          <Search className="h-5 w-5 text-[#ff3df8]" />
          <input className="next-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, phone, email..." />
        </div>
      </div>
      <div className="mt-5 grid gap-3">
        {rows.map((rsvp: any) => (
          <div key={rsvp._id} className="next-card flex flex-wrap items-center justify-between gap-4 p-4">
            <div>
              <div className="text-lg font-bold">{fullName(rsvp.firstName, rsvp.lastName)}</div>
              <div className="text-sm text-[#a99ec0]">Party {rsvp.partySize} · {rsvp.phone || rsvp.email || 'no contact'} · <span className={statusTone(rsvp.status)}>{rsvp.status}</span></div>
            </div>
            <button onClick={() => runCheckIn(rsvp._id)} disabled={rsvp.status === 'checked_in'} className="next-gradient-button px-4 py-3 text-xs disabled:opacity-50">
              {rsvp.status === 'checked_in' ? 'Checked in' : 'Check in'}
            </button>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function NextEventsRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/next" element={<LandingPage />} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/next/join" element={<JoinPage />} />
      <Route path="/e/:eventSlug" element={<PublicEventPage />} />
      <Route path="/next/e/:eventSlug" element={<PublicEventPage />} />
      <Route path="/r/:code" element={<ReferralResolver />} />
      <Route path="/next/r/:code" element={<ReferralResolver />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/admin" element={<NextProtectedRoute><AdminPage /></NextProtectedRoute>} />
      <Route path="/next/admin" element={<NextProtectedRoute><AdminPage /></NextProtectedRoute>} />
      <Route path="/promoter" element={<NextProtectedRoute><PromoterPage /></NextProtectedRoute>} />
      <Route path="/next/promoter" element={<NextProtectedRoute><PromoterPage /></NextProtectedRoute>} />
      <Route path="/door/:eventSlug" element={<NextProtectedRoute><DoorPage /></NextProtectedRoute>} />
      <Route path="/next/door/:eventSlug" element={<NextProtectedRoute><DoorPage /></NextProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function isNextEventsExperience() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'next.event.tools' || host === 'www.next.event.tools' || window.location.pathname === '/next' || window.location.pathname.startsWith('/next/');
}

export function NextEventsApp() {
  return (
    <ErrorBoundary fallback={<NextEventsUnavailable />}>
      <NextEventsRoutes />
    </ErrorBoundary>
  );
}
